import React from 'react';
import { Helmet } from 'react-helmet-async';

const BlogSEO = ({ post, isBlogListing = false, allPosts = [] }) => {
  const baseUrl = 'https://nypestexterminating.com';
  const defaultImage = 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1200&h=630&fit=crop';
  const businessName = 'NY Pest & Exterminating';
  const defaultAuthor = 'NY Pest & Exterminating Team';
  
  // For blog listing page
  if (isBlogListing) {
    const blogListingSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Pest Control Resources & Tips",
      "description": "Expert advice, seasonal tips, and pest prevention guides from New York's eco-friendly pest control specialists",
      "url": `${baseUrl}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": businessName,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/logo.png`
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": allPosts.map((p, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `${baseUrl}/blog/${p.id}`,
          "name": p.title
        }))
      }
    };

    return (
      <Helmet>
        <title>Pest Control Blog - Tips & Resources | NY Pest & Exterminating</title>
        <meta name="description" content="Expert pest control advice, seasonal tips, and prevention guides from NYC's eco-friendly pest control specialists. Learn how to protect your home naturally." />
        <link rel="canonical" href={`${baseUrl}/blog`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Pest Control Blog - Tips & Resources | NY Pest & Exterminating" />
        <meta property="og:description" content="Expert pest control advice, seasonal tips, and prevention guides from NYC's eco-friendly pest control specialists." />
        <meta property="og:url" content={`${baseUrl}/blog`} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:site_name" content={businessName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pest Control Blog - Tips & Resources" />
        <meta name="twitter:description" content="Expert pest control advice from NYC's eco-friendly specialists" />
        <meta name="twitter:image" content={defaultImage} />
        
        {/* Bing */}
        <meta name="msvalidate.01" content="pest-control-blog-verification" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify(blogListingSchema)}
        </script>
      </Helmet>
    );
  }
  
  // For individual blog post
  if (!post) return null;
  
  const postUrl = `${baseUrl}/blog/${post.id}`;
  const featuredImage = post.featured_image_url || defaultImage;
  const author = post.author || defaultAuthor;
  
  // Calculate read time
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };
  
  const readTime = calculateReadTime(post.content);
  
  // Extract headings for FAQ schema (voice search optimization)
  const extractFAQs = (content) => {
    const lines = content.split('\n');
    const faqs = [];
    let currentQuestion = null;
    let currentAnswer = [];
    
    lines.forEach(line => {
      if (line.startsWith('##')) {
        if (currentQuestion) {
          faqs.push({
            question: currentQuestion,
            answer: currentAnswer.join(' ').trim()
          });
        }
        currentQuestion = line.replace('##', '').trim();
        currentAnswer = [];
      } else if (currentQuestion && line.trim()) {
        currentAnswer.push(line.replace(/^\*\s*/, '').trim());
      }
    });
    
    if (currentQuestion && currentAnswer.length > 0) {
      faqs.push({
        question: currentQuestion,
        answer: currentAnswer.join(' ').trim()
      });
    }
    
    return faqs;
  };
  
  // Combine extracted FAQs from content with admin-added FAQs
  const extractedFAQs = extractFAQs(post.content);
  const adminFAQs = post.faq || [];
  const allFAQs = [...adminFAQs, ...extractedFAQs];
  
  // BlogPosting Schema (Google, Bing, AI Search)
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.content.substring(0, 160).replace(/##|\*/g, ''),
    "image": featuredImage,
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": businessName,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": postUrl
    },
    "url": postUrl,
    "wordCount": post.content.split(/\s+/).length,
    "timeRequired": `PT${readTime}M`,
    "articleBody": post.content
  };
  
  // Article Schema (Alternative for news sites)
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "image": [featuredImage],
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "author": {
      "@type": "Person",
      "name": author,
      "url": baseUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": businessName,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "description": post.content.substring(0, 160).replace(/##|\*/g, '')
  };
  
  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${baseUrl}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": postUrl
      }
    ]
  };
  
  // FAQ Schema for Voice Search (Siri, Google Assistant, Alexa)
  const faqSchema = allFAQs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allFAQs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;
  
  // Voice Search Keywords - Enhanced with specific rodent extermination terms
  const baseKeywords = `how to ${post.title.toLowerCase()}, what is ${post.title.toLowerCase()}, ${post.title.toLowerCase()} near me, best way to ${post.title.toLowerCase()}, ${post.title.toLowerCase()} tips`;
  
  const rodentKeywords = `rodent extermination NYC, rat removal New York City, mice exterminator near me, emergency rat control NYC, apartment rodent control Manhattan, commercial rat extermination Brooklyn, same-day mouse exterminator Queens, Hey Google find a rodent exterminator near me, Siri emergency rat removal NYC, licensed rodent control Staten Island, affordable mouse extermination Bronx, rodent proofing services NYC, best rat exterminator New York City, rodent inspection free quote, how to prevent pests in winter`;
  
  const voiceKeywords = `${baseKeywords}, ${rodentKeywords}, pest control near me, exterminator NYC, bed bugs NYC, termite inspection, eco-friendly pest control, 24 hour pest control, emergency exterminator`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{post.title} | NY Pest & Exterminating Blog</title>
      <meta name="description" content={post.content.substring(0, 160).replace(/##|\*/g, '')} />
      <link rel="canonical" href={postUrl} />
      <meta name="keywords" content={`pest control, ${post.title.toLowerCase()}, NYC pest control, eco-friendly pest control, ${voiceKeywords}`} />
      
      {/* Author & Publisher */}
      <meta name="author" content={author} />
      <meta name="publisher" content={businessName} />
      
      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.content.substring(0, 160).replace(/##|\*/g, '')} />
      <meta property="og:url" content={postUrl} />
      <meta property="og:image" content={featuredImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={businessName} />
      <meta property="article:published_time" content={post.created_at} />
      <meta property="article:modified_time" content={post.updated_at} />
      <meta property="article:author" content={author} />
      <meta property="article:section" content="Pest Control" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.content.substring(0, 160).replace(/##|\*/g, '')} />
      <meta name="twitter:image" content={featuredImage} />
      <meta name="twitter:site" content="@NYPestControl" />
      <meta name="twitter:creator" content="@NYPestControl" />
      
      {/* Apple-Specific Meta Tags (Apple Search, Safari) */}
      <meta name="apple-mobile-web-app-title" content={post.title} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=yes" />
      <link rel="apple-touch-icon" href={featuredImage} />
      
      {/* Bing / Microsoft */}
      <meta name="msapplication-TileImage" content={featuredImage} />
      <meta name="msapplication-TileColor" content="#0d7916" />
      
      {/* Voice Search Optimization */}
      <meta name="voice-search" content={voiceKeywords} />
      <meta name="speakable" content="true" />
      
      {/* AI Search Optimization (ChatGPT, Perplexity, Claude) */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="mobile-web-app-capable" content="yes" />
      
      {/* Reading Time */}
      <meta name="reading-time" content={`${readTime} minutes`} />
      
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(blogPostingSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default BlogSEO;
